import React, { useState } from "react";
import Avatar from "../../../components/Avatar";
import { convertToDateWithPatterRaw, DATE_PATTERN_SLASH_DD_MM_YYYY, isProd } from "../../../helper/helper";
import { SupplierOpportunity } from "../../../models/supplier/SupplierOpportunity";
import { colorGreen500, colorGrey, colorPrimary, colorYellowMedium } from "../../../rsc/colors/colors";
import { ReactComponent as Drop } from "../../../rsc/drop.svg";
import { ReactComponent as Fruit } from "../../../rsc/fruit.svg";
import { ReactComponent as MillIcon } from "../../../rsc/ic_mill.svg";
import { ReactComponent as Check } from "../../../rsc/ic_round_check.svg";
import { ReactComponent as Pempem } from "../../../rsc/logo_pempem.svg";
import { ReactComponent as Weight } from "../../../rsc/weight.svg";
import { OpportunityStatusButton } from "./OpportunityStatusButton";
import { ReactComponent as PhoneIcon } from "../../../rsc/ic_phone.svg";
import Whatsapp from "../../../rsc/whatsapp.png"

type BigOpportunityProps = {
    opp: SupplierOpportunity
    reload?: () => void
}

function generateCirclePolygon(center: [number, number], radiusMeters: number, points: number = 60): GeoJSON.Feature<GeoJSON.Polygon> {
    const [lng, lat] = center;
    const coordinates: [number, number][] = [];
    const earthRadius = 6378137; // meters

    for (let i = 0; i < points; i++) {
        const angle = (i / points) * 2 * Math.PI;
        const dx = radiusMeters * Math.cos(angle);
        const dy = radiusMeters * Math.sin(angle);

        // Offset in degrees
        const deltaLng = dx / (earthRadius * Math.cos(Math.PI * lat / 180) * Math.PI / 180);
        const deltaLat = dy / (earthRadius * Math.PI / 180);

        coordinates.push([lng + deltaLng, lat + deltaLat]);
    }
    coordinates.push(coordinates[0]); // close the polygon

    return {
        type: "Feature",
        geometry: {
            type: "Polygon",
            coordinates: [coordinates]
        },
        properties: {}
    };
}

// Returns a Mapbox static image URL with the polygon overlay
export function getMapboxStaticCircleImage(center: [number, number], radiusMeters: number, width: number, height: number, mapboxToken: string, drawCircle = true): string {
    if (center) {
        const polygon = generateCirclePolygon(center, radiusMeters, 60);
        const featureCollection = {
            type: "FeatureCollection",
            features: [
                {
                    ...polygon,
                    properties: {
                        fill: "#15A9AF",
                        "stroke-width": 3,
                        "fill-opacity": 0.2
                    }
                }
            ]
        };
        const geojson = encodeURIComponent(JSON.stringify(featureCollection));
        const overlay = drawCircle ? `/geojson(${geojson})` : "";

        // Center the map on the point
        const [lng, lat] = center;
        const zoom = 10; // adjust as needed

        // Use your custom style if you want to style by properties
        return `https://api.mapbox.com/styles/v1/mapbox/streets-v11/static${overlay}/${lng},${lat},${zoom}/${width}x${height}?access_token=${mapboxToken}`;
    }
}


const iconSize = 35
export function BigOpportunity({ opp, reload }: BigOpportunityProps) {

    const [opportunity, setOpportunity] = useState(opp)

    return (
        <div>
            <div className="pempem-flex-col boxShadow lightBgColor" style={{ border: '2px solid grey', borderRadius: '5px' }}>
                <div className="pempem-flex-row gap5" style={{ alignItems: 'center', borderBottom: '1px solid grey', padding: '0 10px' }}>
                    <MillIcon width={40} />
                    <span className="bold font-20">{opportunity.opportunity?.big_opp?.org?.name}</span>
                    <span hidden={isProd()}>{opportunity.opportunity.id}</span>
                </div>
                <div className="pempem-flex-row gap10 padding10" style={{ width: 450 }}>
                    <div className="pempem-flex-col gap10" style={{ flex: 1 }}>
                        <div className="pempem-flex-row-rev gap5">
                            <div className="pempem-flex-row">
                                <Weight />
                                <div className="pempem-flex-col">
                                    <span className="bold font-20">{opportunity?.opportunity?.big_opp?.tonnage}</span>
                                    <span className="bold font-14">/t/hari</span>
                                </div>
                            </div>
                            <div className="pempem-flex-row" style={{ alignItems: 'center' }}>
                                {
                                    opportunity?.opportunity?.big_opp?.extraction_rate ?
                                        <div className="pempem-flex-row">
                                            <Drop />
                                            <div className="pempem-flex-col-center">
                                                <span className="bold font-20">{opportunity?.opportunity?.big_opp?.extraction_rate}<span className="bold font-12">%</span></span>
                                                <span className="bold font-14">OER<Check fill={opportunity.opportunity?.isRejected() ? colorGrey() : colorGreen500} width={14} /></span>
                                            </div>
                                        </div>
                                        :
                                        <div className="pempem-flex-row">
                                            <Fruit fill={"#FEBD2B"}/>
                                            <div className="pempem-flex-col-center">
                                                <span className="bold font-20">{opportunity?.opportunity?.big_opp?.tenera_ratio}<span className="bold font-12">%</span></span>
                                                <span className="bold font-14">tenera</span>
                                            </div>
                                        </div>
                                }
                            </div>
                        </div>
                        <div className="pempem-flex-col padding5 gap5 lightColor" style={{ background: colorYellowMedium(), borderRadius: 10, alignItems: 'center' }}>
                            <span className="font-20 bold ">{opportunity?.opportunity?.big_opp?.current_price} <span className="font-14 bold">RP/KG</span></span>
                            <span className="font-14">({convertToDateWithPatterRaw(opportunity?.opportunity?.updated_at, DATE_PATTERN_SLASH_DD_MM_YYYY)})</span>
                        </div>
                        {
                            opportunity.field_agent &&
                            <div className="pempem-flex-col-center gap5">
                                <span className="bold" style={{ color: colorPrimary() }}>Your <Pempem fill={colorPrimary()} /> Pempem Agent</span>
                                <div className="pempem-flex-row gap5" style={{ alignItems: 'center' }}>
                                    <Avatar style={{ width: 50, height: 50, fontSize: '20px', flexShrink: 0 }} firstname={opportunity?.field_agent?.firstname} lastname={opportunity?.field_agent?.lastname} uri={opportunity.field_agent?.user?.avatar_url} />
                                    <span className="primaryDarkColor font-20 bold">{opportunity.field_agent?.getFullName()}</span>
                                </div>
                                {
                                    opportunity.field_agent.phone_number &&
                                    <div className="pempem-flex-row-center gap10">
                                        <a href={`https://wa.me/${opportunity.field_agent.phone_number}`}>
                                            <div style={{ border: `solid 2px #00BB46`, borderRadius: '5px', width: iconSize, height: iconSize }} className="padding5 pempem-flex-row-center">
                                                <img src={Whatsapp} width={20} />
                                            </div>
                                        </a>
                                        <a href={`tel:${opportunity.field_agent.phone_number}`}>
                                            <div style={{ border: `solid 2px ${colorPrimary()}`, borderRadius: '5px', width: iconSize, height: iconSize }} className="padding5 pempem-flex-row-center">
                                                <PhoneIcon fill={colorPrimary()} />
                                            </div>
                                        </a>
                                    </div>
                                }
                            </div>
                        }
                    </div>

                    <div style={{ flex: 1, position: 'relative', justifyItems: 'center' }}>
                        {opportunity?.opportunity?.big_opp?.contact_position ?
                            <img style={{width: '100%', height: '100%', objectFit:'cover'}} src={getMapboxStaticCircleImage(opportunity?.opportunity?.big_opp?.contact_position?.coordinates, 2000, 250, 250, process.env.REACT_APP_MAPBOX_TOKEN)}></img>
                            :
                            <div className="pempem-flex-row-center" style={{ width: 200, height: '200px', position: "relative" }}>
                                <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, right: 0 }}>
                                    <img
                                        src={getMapboxStaticCircleImage(opportunity?.opportunity?.big_opp?.org?.positionepsg4326?.coordinates, 600, 250, 250, process.env.REACT_APP_MAPBOX_TOKEN, false)}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                </div>
                                <div style={{ position: "absolute", top: 0, bottom: 0, right: 0, left: 0, background: '#ffffff77' }} />
                                {/* <PemBoyStick style={{ zIndex: 1000 }} width={100} height={100} /> */}
                            </div>
                        }
                        {opportunity?.opportunity?.isRejected() &&
                            <div style={{ position: "absolute", top: 0, bottom: 0, right: 0, left: 0, background: '#ffffff77' }} />
                        }
                    </div>
                </div>
                <div style={{ paddingBottom: 10 }}>
                    <OpportunityStatusButton onReject={reload} opportunity={opportunity?.opportunity} onAccept={(o) => setOpportunity(o)} />
                </div>
            </div>
        </div>)
}